import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UseThinkingDisplayParams {
  isThinking: boolean;
  message: string;
  steps: string[];
  startedAtMs: number | null;
}

interface ThinkingDisplay {
  elapsedSeconds: number;
  displayLabel: string;
  finalSeconds: number | null;
}

const useThinkingDisplay = ({ isThinking, message, steps, startedAtMs }: UseThinkingDisplayParams): ThinkingDisplay => {
  const { t } = useTranslation();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [finalSeconds, setFinalSeconds] = useState<number | null>(null);
  // Mirrors the anchor so finalSeconds is still computable after runtime clears it on finishSend.
  const lastAnchorRef = useRef<number | null>(null);
  const wasThinkingRef = useRef(false);

  useEffect(() => {
    if (startedAtMs !== null) lastAnchorRef.current = startedAtMs;
  }, [startedAtMs]);

  useEffect(() => {
    if (isThinking && startedAtMs !== null) {
      setFinalSeconds(null);
      const tick = () => {
        const seconds = Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000));
        setElapsedSeconds(seconds);
      };
      tick();
      const id = window.setInterval(tick, 250);
      wasThinkingRef.current = true;
      return () => window.clearInterval(id);
    }

    if (wasThinkingRef.current) {
      const anchor = lastAnchorRef.current;
      if (anchor !== null) {
        const seconds = Math.max(1, Math.floor((Date.now() - anchor) / 1000));
        setFinalSeconds(seconds);
      }
      wasThinkingRef.current = false;
    }
    return undefined;
  }, [isThinking, startedAtMs]);

  const fallback = steps.length > 0 ? steps[steps.length - 1] : t('Agent.DefaultThinkingLabel');
  const displayLabel = message?.trim().length > 0 ? message : fallback;

  return { elapsedSeconds, displayLabel, finalSeconds };
};

export default useThinkingDisplay;
